import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import TaskStageBadge from "../components/TaskStageBadge";
import {
  Search,
  Star,
  CheckCircle2,
  Clock,
  AlertTriangle,
  Eye,
  Palette,
  ChevronUp,
  ChevronDown,
  Calendar,
} from "lucide-react";

const SPECIALIZATIONS = [
  { value: "exterior", label: "Exterior", color: "#3b82f6" },
  { value: "interior", label: "Interior", color: "#8b5cf6" },
  { value: "aerial", label: "Aerial", color: "#06b6d4" },
  { value: "animation", label: "Animation", color: "#f59e0b" },
  { value: "floor_plan", label: "Floor Plan", color: "#10b981" },
  { value: "modeller", label: "Modeller", color: "#ec4899" },
  { value: "other", label: "Other", color: "#6b7280" },
];

const PartnerArtistsPage = () => {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [availabilityFilter, setAvailabilityFilter] = useState("all");
  const [specializationFilter, setSpecializationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("trp_active");
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [tasksTab, setTasksTab] = useState("active");

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const res = await api.get("/partner/artists");
      setArtists(res.data || []);
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to load artists");
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityBadge = (artist) => {
    if (artist.is_overloaded) {
      return (
        <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Busy
        </Badge>
      );
    }
    if (artist.is_free) {
      return (
        <Badge variant="outline" className="bg-green-500/10 text-green-400 border-green-500/20 text-xs">
          <CheckCircle2 className="h-3 w-3 mr-1" />
          Free
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="bg-sky-500/10 text-sky-400 border-sky-500/20 text-xs">
        <Clock className="h-3 w-3 mr-1" />
        Working
      </Badge>
    );
  };

  const getWorkloadBar = (percent) => {
    let colorClass = "bg-green-500";
    if (percent >= 80) colorClass = "bg-red-500";
    else if (percent >= 50) colorClass = "bg-orange-500";
    else if (percent >= 25) colorClass = "bg-sky-500";

    return (
      <div className="w-20">
        <Progress value={percent} className="h-1.5 bg-zinc-800" indicatorClassName={colorClass} />
      </div>
    );
  };

  let filteredArtists = artists.filter((a) => {
    const matchesSearch = a.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesAvailability =
      availabilityFilter === "all" ||
      (availabilityFilter === "free" && a.is_free) ||
      (availabilityFilter === "working" && !a.is_free && !a.is_overloaded) ||
      (availabilityFilter === "overloaded" && a.is_overloaded);
    const matchesSpec =
      specializationFilter === "all" ||
      (a.specializations || []).includes(specializationFilter);
    return matchesSearch && matchesAvailability && matchesSpec;
  });

  filteredArtists = filteredArtists.sort((a, b) => {
    if (sortBy === "trp_active") return b.trp_tasks_active - a.trp_tasks_active;
    if (sortBy === "workload") return a.total_active - b.total_active;
    if (sortBy === "name") return a.name.localeCompare(b.name);
    if (sortBy === "rating") return b.rating - a.rating;
    return 0;
  });

  const totalArtists = artists.length;
  const freeArtists = artists.filter((a) => a.is_free).length;
  const busyArtists = artists.filter((a) => a.is_overloaded).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="partner-artists-page" className="space-y-5">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope'] flex items-center gap-3">
          <Palette className="h-7 w-7 text-orange-500" />
          Artists
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          {totalArtists} artists working on your projects
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Total Artists</p>
            <p className="text-2xl font-bold text-zinc-50 mt-1">{totalArtists}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Available</p>
            <p className="text-2xl font-bold text-green-400 mt-1">{freeArtists}</p>
          </CardContent>
        </Card>
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono">Busy</p>
            <p className="text-2xl font-bold text-red-400 mt-1">{busyArtists}</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search artists..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 h-9"
            data-testid="search-partner-artists"
          />
        </div>
        <Select value={specializationFilter} onValueChange={setSpecializationFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Specialization" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Specs</SelectItem>
            {SPECIALIZATIONS.map((spec) => (
              <SelectItem key={spec.value} value={spec.value} className="text-zinc-300">
                {spec.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={availabilityFilter} onValueChange={setAvailabilityFilter}>
          <SelectTrigger className="w-full sm:w-36 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Availability" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All</SelectItem>
            <SelectItem value="free" className="text-zinc-300">Available</SelectItem>
            <SelectItem value="working" className="text-zinc-300">Working</SelectItem>
            <SelectItem value="overloaded" className="text-zinc-300">Busy</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="trp_active" className="text-zinc-300">TRP Active</SelectItem>
            <SelectItem value="workload" className="text-zinc-300">Workload</SelectItem>
            <SelectItem value="name" className="text-zinc-300">Name</SelectItem>
            <SelectItem value="rating" className="text-zinc-300">Rating</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Artists Table */}
      <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-zinc-800 hover:bg-transparent">
              <TableHead className="text-zinc-400 font-medium">Artist</TableHead>
              <TableHead className="text-zinc-400 font-medium">Specializations</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Rating</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">TRP Active</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">TRP Done</TableHead>
              <TableHead className="text-zinc-400 font-medium">Workload</TableHead>
              <TableHead className="text-zinc-400 font-medium text-center">Stage</TableHead>
              <TableHead className="text-zinc-400 font-medium w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredArtists.map((artist) => (
              <TableRow
                key={artist.id}
                className="border-zinc-800 hover:bg-zinc-800/50"
                data-testid={`partner-artist-row-${artist.id}`}
              >
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-medium text-zinc-300">
                        {artist.name.charAt(0)}
                      </span>
                    </div>
                    <p className="text-zinc-50 font-medium text-sm">{artist.name}</p>
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 flex-wrap">
                    {(artist.specializations || []).map((spec) => {
                      const specConfig = SPECIALIZATIONS.find((s) => s.value === spec);
                      return (
                        <span
                          key={spec}
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: `${specConfig?.color}25`,
                            color: specConfig?.color,
                            border: `1px solid ${specConfig?.color}50`,
                          }}
                        >
                          {specConfig?.label || spec}
                        </span>
                      );
                    })}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <div className="flex items-center justify-center gap-1">
                    <Star
                      className={cn(
                        "h-3.5 w-3.5",
                        artist.rating >= 8
                          ? "text-yellow-400 fill-yellow-400"
                          : "text-zinc-600"
                      )}
                    />
                    <span
                      className={cn(
                        "font-mono text-sm",
                        artist.rating >= 8 ? "text-yellow-400" : "text-zinc-400"
                      )}
                    >
                      {artist.rating}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={cn(
                      "font-mono font-medium",
                      artist.trp_tasks_active > 0
                        ? "text-amber-400"
                        : "text-zinc-600"
                    )}
                  >
                    {artist.trp_tasks_active}
                  </span>
                </TableCell>
                <TableCell className="text-center">
                  <span
                    className={cn(
                      "font-mono",
                      artist.trp_tasks_completed > 0
                        ? "text-green-400"
                        : "text-zinc-600"
                    )}
                  >
                    {artist.trp_tasks_completed}
                  </span>
                </TableCell>
                <TableCell>{getWorkloadBar(artist.workload_percent)}</TableCell>
                <TableCell className="text-center">
                  {getAvailabilityBadge(artist)}
                </TableCell>
                <TableCell>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setSelectedArtist(artist);
                      setTasksTab("active");
                    }}
                    className="h-7 w-7 p-0 text-zinc-400 hover:text-sky-400 hover:bg-zinc-800"
                    data-testid={`view-artist-tasks-${artist.id}`}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredArtists.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-zinc-500 py-12">
                  No artists found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Artist Tasks Dialog */}
      <Dialog
        open={!!selectedArtist}
        onOpenChange={(open) => !open && setSelectedArtist(null)}
      >
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-3xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Palette className="h-5 w-5 text-orange-400" />
              {selectedArtist?.name} — TRP Tasks
            </DialogTitle>
          </DialogHeader>
          {selectedArtist && (
            <div className="space-y-4">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setTasksTab("active")}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    tasksTab === "active"
                      ? "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                      : "bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                  )}
                  data-testid="partner-tasks-tab-active"
                >
                  Active ({selectedArtist.trp_tasks_active})
                </button>
                <button
                  onClick={() => setTasksTab("completed")}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-colors",
                    tasksTab === "completed"
                      ? "bg-green-500/20 text-green-400 border border-green-500/30"
                      : "bg-zinc-800 text-zinc-400 hover:text-zinc-50"
                  )}
                  data-testid="partner-tasks-tab-completed"
                >
                  Completed ({selectedArtist.trp_tasks_completed})
                </button>
              </div>

              {/* Task List */}
              <div className="max-h-[400px] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400 text-xs">Task</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Project</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Type</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Stage</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Deadline</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(() => {
                      const tasks =
                        tasksTab === "active"
                          ? (selectedArtist.trp_tasks || []).filter((t) => !t.is_done)
                          : (selectedArtist.trp_tasks || []).filter((t) => t.is_done);

                      if (tasks.length === 0) {
                        return (
                          <TableRow>
                            <TableCell
                              colSpan={5}
                              className="text-center text-zinc-500 py-8"
                            >
                              {tasksTab === "active"
                                ? "No active tasks"
                                : "No completed tasks"}
                            </TableCell>
                          </TableRow>
                        );
                      }

                      return tasks.map((task) => {
                        const isOverdue =
                          task.deadline &&
                          new Date(task.deadline) < new Date() &&
                          !task.is_done;

                        return (
                          <TableRow
                            key={task.id}
                            className="border-zinc-800 hover:bg-zinc-800/30"
                          >
                            <TableCell className="text-sm text-zinc-50">
                              {task.name?.substring(0, 45)}
                              {task.name?.length > 45 ? "..." : ""}
                            </TableCell>
                            <TableCell className="text-xs text-zinc-400">
                              {task.project_name || "—"}
                            </TableCell>
                            <TableCell className="text-xs text-zinc-400">
                              {task.task_type || "—"}
                            </TableCell>
                            <TableCell>
                              <TaskStageBadge task={task} size="sm" />
                            </TableCell>
                            <TableCell className="text-right">
                              {task.deadline ? (
                                <span
                                  className={cn(
                                    "text-xs flex items-center justify-end gap-1",
                                    isOverdue
                                      ? "text-red-400"
                                      : "text-zinc-500"
                                  )}
                                >
                                  <Calendar className="h-3 w-3" />
                                  {task.deadline}
                                </span>
                              ) : (
                                <span className="text-xs text-zinc-600">—</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      });
                    })()}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PartnerArtistsPage;
